package fyi.moko.hotpl.ui.changelog

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.domain.model.ChangelogEntry
import fyi.moko.hotpl.domain.model.RequestStatus
import fyi.moko.hotpl.domain.model.ReviewerRequest
import fyi.moko.hotpl.domain.model.ReviewerRequests
import org.koin.compose.viewmodel.koinViewModel

/**
 * What changed in each build, for the people testing it.
 *
 * Opening this screen is what clears the "something is new" cue on Home — see
 * [ChangelogViewModel.markSeen].
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChangelogScreen(
    onBack: () -> Unit,
    viewModel: ChangelogViewModel = koinViewModel(),
) {
    LaunchedEffect(Unit) { viewModel.markSeen() }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("What's new") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                },
            )
        },
    ) { padding ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .testTag("changelog-list"),
            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            item(key = "intro") {
                Text(
                    text = "This build is an alpha. Everything below is new since the last one — " +
                        "if something here doesn't behave as described, that's worth reporting.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            viewModel.entries.firstOrNull()?.let { newest ->
                item(key = newest.version) { ChangelogEntryCard(newest) }
            }

            // 리뷰어가 준 피드백이 지난 빌드 기록보다 위에 온다. 아래쪽은 이미 읽은 아카이브고,
            // 이 목록은 "내가 말한 게 어떻게 됐나"라서 매번 다시 보게 되는 쪽이다.
            item(key = "requests") { ReviewerRequestSection() }

            items(viewModel.entries.drop(1), key = { it.version }) { entry ->
                ChangelogEntryCard(entry)
            }
        }
    }
}

/**
 * 리뷰어 요청이 지금 어디까지 왔는지.
 *
 * 상태 하나에 한 줄 설명을 반드시 붙이는 이유는 [ReviewerRequests] 참고 — 구글·애플에서
 * 되는 일이 여기서 안 될 때, 이유 없는 "진행중"은 고장으로 읽힌다.
 */
@Composable
private fun ReviewerRequestSection(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.fillMaxWidth().testTag("changelog-requests"),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = "What you asked for",
            style = MaterialTheme.typography.headlineSmall,
        )
        Text(
            text = "Every piece of feedback so far, and where it stands.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        HorizontalDivider()
        ReviewerRequests.all.forEach { request ->
            ReviewerRequestRow(request)
        }
    }
}

@Composable
private fun ReviewerRequestRow(request: ReviewerRequest, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.fillMaxWidth().padding(top = 4.dp),
        verticalArrangement = Arrangement.spacedBy(3.dp),
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            StatusBadge(request.status)
            request.effort?.let { effort ->
                Text(
                    text = effort.label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        Text(text = request.request, style = MaterialTheme.typography.bodyMedium)
        Text(
            text = request.note,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun StatusBadge(status: RequestStatus) {
    // Done recedes, unfinished work stands out: a reviewer scanning the list is looking for
    // what is still open, not re-reading the wins.
    val color = when (status) {
        RequestStatus.DONE -> MaterialTheme.colorScheme.onSurfaceVariant
        RequestStatus.IN_PROGRESS -> MaterialTheme.colorScheme.primary
        RequestStatus.RETHINKING -> MaterialTheme.colorScheme.error
    }
    Text(
        text = status.label.uppercase(),
        style = MaterialTheme.typography.labelSmall,
        color = color,
        modifier = Modifier
            .border(1.dp, color, RoundedCornerShape(4.dp))
            .padding(horizontal = 6.dp, vertical = 2.dp),
    )
}

@Composable
private fun ChangelogEntryCard(entry: ChangelogEntry) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = entry.date,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(text = entry.headline, style = MaterialTheme.typography.headlineSmall)
        HorizontalDivider()
        entry.changes.forEach { change ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.Top,
            ) {
                Text(
                    text = "•",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.primary,
                )
                Text(text = change, style = MaterialTheme.typography.bodyMedium)
            }
        }
    }
}
